<template>
    <div class="promotion-v3-container">
        <v-app class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Header -->
            <div class="d-flex flex-row align-items-center" :style="{ marginTop: '10px', marginBottom: normalizeSize(20, true) }">
                <h1
                    class="ppv2-text-black poppinsRegular uppercaseFirstLetter mb-0 title-header"
                    :style="{ fontSize: normalizeFontSize(26) }"
                >
                    {{ promotionId ? $t('promotions.edit') : $t('promotions.add') }}
                </h1>
            </div>
            <div class="ppv2-w-full">
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Left Column -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <!-- Main Information -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('promotions.main_information') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-flex ppv2-flex-wrap ppv2-p-6">
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('promotions.code')"
                                        v-model="form.code"
                                        :required="true"
                                        :placeholder="$t('promotions.code')"
                                        :disabled="form.generate_randomly"
                                        :errorText="errors.code ? errors.code[0] : ''"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                    <div v-if="!promotionId" class="ppv2-mt-1">
                                        <a
                                            href="javascript:void(0)"
                                            @click="generateRandomCode"
                                            class="poppinsLight text-purple text-decoration-underline"
                                            :style="{ fontSize: normalizeFontSize(12) }"
                                        >
                                            {{ $t('promotions.generate_randomly') }}
                                        </a>
                                    </div>
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('promotions.title')"
                                        v-model="form.title"
                                        :placeholder="$t('promotions.title')"
                                        :required="true"
                                        :errorText="errors.title ? errors.title[0] : ''"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                            </div>
                        </v-card>

                        <!-- Discount and Dates -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('promotions.discount_details') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>

                            <div class="ppv2-flex ppv2-flex-wrap ppv2-p-6">
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <BaseSelect
                                        :label="$t('promotions.discount_type')"
                                        v-model="form.discount_type"
                                        :options="[
                                            { label: $t('promotions.percentage') + ' (%)', value: 'percentage' },
                                            {
                                                label: $t('promotions.fixed_amount') + ' (' + $symbolCurrency + ')',
                                                value: 'fixed',
                                            },
                                        ]"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="
                                            form.discount_type === 'percentage'
                                                ? $t('promotions.discount_pourcent')
                                                : $t('promotions.discount_amount')
                                        "
                                        v-model="form.discount_amount"
                                        type="number"
                                        step="0.01"
                                        :rightIcon="form.discount_type === 'percentage' ? '%' : $symbolCurrency"
                                        :max="form.discount_type === 'percentage' ? 100 : undefined"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :errorText="errors.discount_amount ? errors.discount_amount[0] : ''"
                                    />
                                </div>
                            </div>
                        </v-card>

                        <!-- Limits -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('promotions.title_eligibility') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>

                            <div class="ppv2-flex ppv2-flex-wrap ppv2-p-6 ppv2-gap-y-6">
                                <div
                                    class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3 ppv2-flex ppv2-items-center"
                                    :style="{ gap: normalizeSize(16, true) }"
                                >
                                    <CustomInput
                                        class="ppv2-flex-1"
                                        :label="$t('promotions.total_uses')"
                                        v-model="form.uses"
                                        :required="!form.unlimited_uses"
                                        :disabled="form.unlimited_uses"
                                        type="number"
                                        :errorText="errors.uses ? errors.uses[0] : ''"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        classContainerAdd="mb-0"
                                    />
                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-mt-6">
                                        <BaseSwitch
                                            :label="$t('promotions.unlimited_uses')"
                                            v-model="form.unlimited_uses"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                        />
                                    </div>
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('promotions.uses_per_user')"
                                        v-model="form.uses_per_user"
                                        type="number"
                                        min="1"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :errorText="errors.uses_per_user ? errors.uses_per_user[0] : ''"
                                        classContainerAdd="mb-0"
                                    />
                                </div>

                                <!-- Row 2 Dates -->
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <BaseDateInput
                                        :label="$t('promotions.start_date')"
                                        v-model="form.start_date"
                                        :defaultValue="form.start_date"
                                        :required="true"
                                        :disabled="form.used > 0 ? true : false"
                                        :minDate="startDateMinDate"
                                        :errorText="errors.start_date ? errors.start_date[0] : ''"
                                        :customSizeLabel="15"
                                        inputClass="!ppv2-rounded-lg !ppv2-h-[35px] !ppv2-min-h-[35px]"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>

                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <BaseDateInput
                                        :label="$t('promotions.end_date')"
                                        :required="false"
                                        :defaultValue="tomorrow"
                                        v-model="form.expiration_date"
                                        :minDate="form.start_date || tomorrow"
                                        :errorText="errors.expiration_date ? errors.expiration_date[0] : ''"
                                        :customSizeLabel="15"
                                        inputClass="!ppv2-rounded-lg !ppv2-h-[35px] !ppv2-min-h-[35px]"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>

                                <!-- Row 3: Répétition par jours -->
                                <div class="ppv2-w-full ppv2-px-3 ppv2-mb-4">
                                    <h3
                                        class="poppinsLight ppv2-text-black ppv2-mb-2"
                                        :style="{ fontSize: normalizeFontSize(15) }"
                                    >
                                        {{ $t('promotions.repetition_by_days') }}
                                    </h3>
                                    <div class="ppv2-flex" :style="{ gap: normalizeSize(16, true) }">
                                        <button
                                            v-for="(day, index) in weekDays"
                                            :key="day.value"
                                            type="button"
                                            class="day-circle-btn poppinsRegular"
                                            :class="{ active: form.days.includes(day.value) }"
                                            @click="toggleDay(day.value)"
                                            :style="{
                                                width: normalizeSize(40, true),
                                                height: normalizeSize(40, true),
                                                fontSize: normalizeFontSize(16),
                                            }"
                                        >
                                            {{ day.name }}
                                        </button>
                                    </div>
                                    <div v-if="Object.keys(errors).some((key) => key.startsWith('days.'))" class="ppv2-mt-2">
                                        <span class="ppv2-text-red-500 text-sm">
                                            {{ errors[Object.keys(errors).find((key) => key.startsWith('days.'))][0] }}
                                        </span>
                                    </div>
                                </div>

                                <!-- Row 4 Amount -->
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('promotions.min_amount')"
                                        v-model="form.min_amount"
                                        type="number"
                                        step="0.01"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :errorText="errors.min_amount ? errors.min_amount[0] : ''"
                                        :rightIcon="$symbolCurrency"
                                    />
                                </div>

                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('promotions.max_amount')"
                                        v-model="form.max_amount"
                                        type="number"
                                        step="0.01"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :errorText="errors.max_amount ? errors.max_amount[0] : ''"
                                    />
                                </div>
                                <!-- Row 4 Toggles -->
                                <div class="ppv2-w-full ppv2-px-3 ppv2-mt-4 ppv2-space-y-4">
                                    <BaseSwitch
                                        :label="$t('promotions.back_office_only')"
                                        v-model="form.back_office_only"
                                        :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                    <div class="ppv2-w-full">
                                        <BaseSwitch
                                            hasBorderChecked
                                            :label="$t('promotions.apply_on_reservation_date')"
                                            v-model="applyOnReservationDate"
                                        />
                                    </div>
                                    <div
                                        v-if="!form.has_linked_items"
                                        class="ppv2-relative"
                                        :style="{
                                            marginTop: normalizeSizeVertical(27, true),
                                            marginBottom: normalizeSizeVertical(20, true),
                                        }"
                                    >
                                        <div>
                                            <BaseSwitch
                                                :label="$t('promotions.has_linked_items')"
                                                v-model="form.has_linked_items"
                                                :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                            />
                                        </div>
                                    </div>

                                    <DashedCard
                                        v-else
                                        :marginTop="37"
                                        :marginBottom="30"
                                        :custom-style="{ padding: normalizeSize(24, true) }"
                                    >
                                        <template #title>
                                            <BaseSwitch
                                                :label="$t('promotions.has_linked_items')"
                                                v-model="form.has_linked_items"
                                                :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                            />
                                        </template>
                                        <template #content>
                                            <div v-if="form.has_linked_items" class="ppv2-w-full ppv2-mt-4">
                                                <div class="ppv2-flex ppv2-flex-wrap">
                                                    <div
                                                        v-for="item in linkedItemsFields"
                                                        :key="item.key"
                                                        class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3 ppv2-mb-4"
                                                    >
                                                        <BaseSelect
                                                            :hidden="item.hidden"
                                                            :label="item.label"
                                                            v-model="form[item.key]"
                                                            :options="item.options"
                                                            optionLabel="name"
                                                            optionValue="id"
                                                            :placeholder="item.placeholder"
                                                            multiple
                                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                                            customLabelClass="poppinsLight ppv2-text-black"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                    </DashedCard>

                                    <div
                                        v-if="!form.limit_order_type"
                                        class="ppv2-relative"
                                        :style="{
                                            marginTop: normalizeSizeVertical(27, true),
                                            marginBottom: normalizeSizeVertical(20, true),
                                        }"
                                    >
                                        <div>
                                            <BaseSwitch
                                                :label="$t('promotions.limit_order_type')"
                                                v-model="form.limit_order_type"
                                                :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                            />
                                        </div>
                                    </div>

                                    <DashedCard
                                        v-else
                                        :marginTop="37"
                                        :marginBottom="30"
                                        :custom-style="{ padding: normalizeSize(24, true) }"
                                    >
                                        <template #title>
                                            <BaseSwitch
                                                :label="$t('promotions.limit_order_type')"
                                                v-model="form.limit_order_type"
                                                :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                            />
                                        </template>
                                        <template #content>
                                            <div
                                                v-if="form.limit_order_type"
                                                class="ppv2-flex ppv2-flex-col"
                                                :style="{ gap: normalizeSize(16, true) }"
                                            >
                                                <div class="ppv2-w-full">
                                                    <BaseCheckbox
                                                        hasBorderChecked
                                                        :label="$t('promotions.order_types.voucher')"
                                                        v-model="form.has_voucher_categories"
                                                    />
                                                </div>
                                                <div class="ppv2-w-full">
                                                    <BaseCheckbox
                                                        hasBorderChecked
                                                        :label="$t('promotions.order_types.prestation')"
                                                        v-model="form.has_prestation_categories"
                                                    />
                                                </div>
                                                <div class="ppv2-w-full">
                                                    <BaseCheckbox
                                                        hasBorderChecked
                                                        :label="$t('promotions.order_types.product')"
                                                        v-model="form.has_product_categories"
                                                    />
                                                </div>
                                                <div class="ppv2-w-full">
                                                    <BaseCheckbox
                                                        hasBorderChecked
                                                        :label="$t('promotions.order_types.food')"
                                                        v-model="form.has_food_categories"
                                                    />
                                                </div>
                                            </div>
                                        </template>
                                    </DashedCard>
                                </div>
                            </div>
                        </v-card>
                    </div>

                    <!-- Right Column -->
                    <div class="ppv2-w-full md:ppv2-w-[35%]">
                        <div
                            class="ppv2-text-black ppv2-rounded-[10px] ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-p-6"
                        >
                            <!-- Toggle -->
                            <div class="ppv2-mb-8">
                                <BaseSwitch
                                    v-model="form.active"
                                    :label="form.active ? $t('common.enabled') : $t('common.disabled')"
                                    :boxed="true"
                                    :variant="form.active ? 'success' : 'gray'"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                            </div>

                            <!-- Details List -->
                            <div class="ppv2-space-y-4">
                                <!-- Code -->
                                <div
                                    class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-3 ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg bg-purple ppv2-bg-opacity-[4%]"
                                >
                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                                        <img src="/media/svg/badgePercent.svg" alt="badgePercent" />
                                        <span
                                            class="ppv2-text-black poppinsRegular"
                                            :style="`font-size: ${normalizeSize(15, true)}`"
                                            >{{ $t('promotions.reviews.code') }}</span
                                        >
                                    </div>
                                    <span class="poppinsMedium" :style="`font-size: ${normalizeSize(15, true)}`">{{
                                        form.code || '-'
                                    }}</span>
                                </div>

                                <!-- Start Date -->
                                <div
                                    class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-3 ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg bg-purple ppv2-bg-opacity-[4%]"
                                >
                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                                        <CalendarDays :size="24" class="text-purple" :stroke-width="1" />
                                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">{{
                                            $t('promotions.start_date')
                                        }}</span>
                                    </div>
                                    <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(15) }">{{
                                        form.start_date || '-'
                                    }}</span>
                                </div>

                                <!-- Uses -->
                                <div
                                    class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-3 ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg bg-purple ppv2-bg-opacity-[4%]"
                                >
                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                                        <CircleUserRound :size="24" class="text-purple" :stroke-width="1" />
                                        <span
                                            class="ppv2-text-black poppinsRegular"
                                            :style="{ fontSize: normalizeFontSize(15) }"
                                            >{{ $t('promotions.number_of_uses') }}</span
                                        >
                                    </div>
                                    <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(15) }">{{
                                        form.unlimited_uses ? $t('promotions.unlimited_uses') : form.uses || '-'
                                    }}</span>
                                </div>

                                <!-- Discount -->
                                <div
                                    class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-3 ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg bg-purple ppv2-bg-opacity-[4%]"
                                >
                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                                        <img src="/media/svg/badgeDiscount.svg" alt="badgeDiscount" />
                                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">{{
                                            $t('promotions.reviews.remise')
                                        }}</span>
                                    </div>
                                    <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(15) }">
                                        {{ form.discount_amount }}
                                        {{ form.discount_type === 'percentage' ? '%' : $symbolCurrency }}
                                    </span>
                                </div>

                                <!-- Expiration -->
                                <div
                                    class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-3 ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg bg-purple ppv2-bg-opacity-[4%]"
                                >
                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                                        <CalendarDays :size="24" class="text-purple" :stroke-width="1" />
                                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">{{
                                            $t('promotions.reviews.expiration')
                                        }}</span>
                                    </div>
                                    <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(15) }">{{
                                        form.expiration_date == null ? $t('promotions.not_defined') : form.expiration_date || '-'
                                    }}</span>
                                </div>

                                <!-- Days of Validity -->
                                <div
                                    class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-3 ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg bg-purple ppv2-bg-opacity-[4%]"
                                >
                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                                        <RotateCcw :size="24" class="text-purple" :stroke-width="1" />
                                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">{{
                                            $t('promotions.repetition_by_days')
                                        }}</span>
                                    </div>
                                    <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(15) }">{{
                                        formattedDays
                                    }}</span>
                                </div>

                                <!-- Icon suggests repeat/cycle. The screenshot label is "Limite d'utilisation". In the form we have "uses_per_user". -->
                                <div
                                    class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-3 ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg bg-purple ppv2-bg-opacity-[4%]"
                                >
                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                                        <RotateCcw :size="24" class="text-purple" :stroke-width="1" />
                                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">{{
                                            $t('promotions.reviews.limit_per_user')
                                        }}</span>
                                    </div>
                                    <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(15) }">{{
                                        !form.uses_per_user || form.uses_per_user == -1
                                            ? $t('promotions.not_defined')
                                            : form.uses_per_user
                                    }}</span>
                                </div>

                                <!-- Min Basket -->
                                <div
                                    class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-3 ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg bg-purple ppv2-bg-opacity-[4%]"
                                >
                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                                        <ShoppingCart :size="24" class="text-purple" :stroke-width="1" />
                                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">{{
                                            $t('promotions.reviews.shop_min_amount')
                                        }}</span>
                                    </div>
                                    <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(15) }">{{
                                        form.min_amount ? form.min_amount + ' ' + $symbolCurrency : $t('promotions.not_defined')
                                    }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Bottom Actions -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <div
                            class="ppv2-flex ppv2-justify-between ppv2-items-center"
                            :style="{ marginTop: normalizeSize(24), marginBottom: normalizeSize(40, true) }"
                        >
                            <BackButton type="button" @click="cancel" />
                            <BaseButton
                                variant="primary"
                                :loading="submitting"
                                @click="submit"
                                :customHeight="48"
                                customClass="!ppv2-min-w-[160px] !ppv2-bg-[#7D2DBD] hover:!ppv2-bg-[#6b26a3]"
                            >
                                {{ $t('common.validate') }}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </div>
        </v-app>

        <!-- Exit Dialog -->
        <CustomDialog v-model="showExitPopup" :defaultTitle="$t('experience.titleClosePopup')" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('experience.descriptionClosePopup') }}
                </p>
            </template>
            <template #actions>
                <BaseButton variant="outline" @click="exit">{{ $t('experience.btnLeave') }}</BaseButton>
                <BaseButton variant="primary" @click="showExitPopup = false">{{ $t('experience.btnStay') }} </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import axios from 'axios'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BackButton from '../../../Shared/BackButton.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseCheckbox from '../../../Shared/BaseCheckbox.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import BaseDateInput from '../../../Shared/BaseDateInput.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import { RotateCcw, ShoppingCart, CalendarDays, CircleUserRound } from 'lucide-vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'PromotionForm',
    components: {
        CustomInput,
        BackButton,
        BaseButton,
        BaseTextarea,
        BaseSelect,
        BaseSwitch,
        BaseCheckbox,
        CustomDialog,
        BaseDateInput,
        CircleUserRound,
        CalendarDays,
        RotateCcw,
        ShoppingCart,
        DashedCard,
    },
    props: {
        userId: {
            type: [Number, String],
            default: null,
        },
        promotionId: {
            type: [Number, String],
            default: null,
        },
        token: {
            type: [String],
            default: '',
        },
    },
    data() {
        return {
            submitting: false,
            loading: false,
            showExitPopup: false,
            form: {
                active: true,
                title: '',
                code: '',
                generate_randomly: false,
                description: '',
                discount_type: 'percentage',
                discount_amount: 0,
                start_date: null,
                expiration_date: null,
                no_expiration_date: false,
                unlimited_uses: false,
                uses: null,
                uses_per_user: 1,
                min_amount: 0,
                max_amount: 0,
                limit_order_type: false,
                back_office_only: false,
                has_linked_items: false,
                prestation_categories: [],
                product_categories: [],
                food_categories: [],
                has_voucher_categories: false,
                has_prestation_categories: false,
                has_product_categories: false,
                has_food_categories: false,
                prestations: [],
                products: [],
                packages: [],
                series: [],
                treatments: [],
                foods: [],
                food_packs: [],
                used: 0,
                days: [],
                apply_to: 'created_at',
            },
            formData: {
                prestations: [],
                products: [],
                series: [],
                treatments: [],
                foods: [],
                foodPacks: [],
                packages: [],
                prestationCategories: [],
                productCategories: [],
                foodCategories: [],
            },
            errors: {},
            initialForm: null,
        }
    },
    computed: {
        tomorrow() {
            const tomorrow = new Date()
            tomorrow.setDate(tomorrow.getDate() + 1)
            tomorrow.setHours(0, 0, 0, 0)
            return tomorrow.toISOString().split('T')[0]
        },
        hasSelectedLinkedItems() {
            const fields = [
                'prestations',
                'products',
                'packages',
                'series',
                'foods',
                'food_packs',
                'treatments',
                'prestation_categories',
                'product_categories',
                'food_categories',
            ]
            return fields.some((field) => this.form[field] && this.form[field].length > 0)
        },
        linkedItemsFields() {
            return [
                {
                    key: 'prestations',
                    label: this.$t('promotions.linked_items.prestations'),
                    options: this.formData.prestations,
                    placeholder: this.$t('promotions.linked_items.prestations'),
                },
                {
                    key: 'products',
                    label: this.$t('promotions.linked_items.products'),
                    options: this.formData.products,
                    placeholder: this.$t('promotions.linked_items.products'),
                },
                {
                    key: 'packages',
                    label: this.$t('promotions.linked_items.combo'),
                    options: this.formData.packages,
                    placeholder: this.$t('promotions.linked_items.combo'),
                },
                {
                    key: 'series',
                    label: this.$t('promotions.linked_items.series'),
                    options: this.formData.series,
                    placeholder: this.$t('promotions.linked_items.series'),
                },
                {
                    key: 'foods',
                    label: this.$t('promotions.linked_items.foods'),
                    options: this.formData.foods,
                    placeholder: this.$t('promotions.linked_items.foods'),
                },
                {
                    key: 'food_packs',
                    label: this.$t('promotions.linked_items.foodPacks'),
                    options: this.formData.foodPacks,
                    placeholder: this.$t('promotions.linked_items.foodPacks'),
                },
                {
                    key: 'treatments',
                    label: 'Traitements',
                    options: this.formData.treatments,
                    placeholder: this.$t('product.category_placeholder'),
                    hidden: true,
                },
                {
                    key: 'prestation_categories',
                    label: 'Catégories de prestations',
                    options: this.formData.prestationCategories,
                    placeholder: this.$t('product.category_placeholder'),
                    hidden: true,
                },
                {
                    key: 'product_categories',
                    label: 'Catégories de produits',
                    options: this.formData.productCategories,
                    placeholder: this.$t('product.category_placeholder'),
                    hidden: true,
                },
                {
                    key: 'food_categories',
                    label: this.$t('promotions.food_categories'),
                    options: this.formData.foodCategories,
                    placeholder: this.$t('product.category_placeholder'),
                    hidden: true,
                },
            ]
        },
        weekDays() {
            return [
                { name: this.$t('promotions.days.mon'), value: 1 },
                { name: this.$t('promotions.days.tue'), value: 2 },
                { name: this.$t('promotions.days.wed'), value: 3 },
                { name: this.$t('promotions.days.thu'), value: 4 },
                { name: this.$t('promotions.days.fri'), value: 5 },
                { name: this.$t('promotions.days.sat'), value: 6 },
                { name: this.$t('promotions.days.sun'), value: 0 },
            ]
        },
        formattedDays() {
            if (!this.form.days || this.form.days.length === 0) {
                return this.$t('promotions.not_defined')
            }
            const dayMap = {
                0: this.$t('promotions.days.sun'),
                1: this.$t('promotions.days.mon'),
                2: this.$t('promotions.days.tue'),
                3: this.$t('promotions.days.wed'),
                4: this.$t('promotions.days.thu'),
                5: this.$t('promotions.days.fri'),
                6: this.$t('promotions.days.sat'),
            }
            const ordered = [1, 2, 3, 4, 5, 6, 0]
            return ordered
                .filter((d) => this.form.days.includes(d))
                .map((d) => dayMap[d])
                .join(', ')
        },
        startDateMinDate() {
            if (this.promotionId && this.form.start_date) {
                const today = new Date().toISOString().split('T')[0]
                return this.form.start_date < today ? this.form.start_date : today
            }
            return this.tomorrow
        },
        applyOnReservationDate: {
            get() {
                return this.form.apply_to === 'reservation_date'
            },
            set(value) {
                this.form.apply_to = value ? 'reservation_date' : 'created_at'
            },
        },
    },
    watch: {
        'form.start_date': function (newStartDate) {
            if (this.form.expiration_date && this.form.expiration_date < newStartDate) {
                this.form.expiration_date = null
                console.log('Expiration date reset to null')
            }
        },
        'form.no_expiration_date'(newVal) {
            if (newVal) {
                this.form.expiration_date = null
            }
        },
        'form.unlimited_uses'(newVal) {
            if (newVal) {
                this.form.uses = null
            }
        },
        'form.discount_amount'(newVal) {
            if (this.form.discount_type === 'percentage' && newVal > 100) {
                this.form.discount_amount = 100
            }
        },
        'form.discount_type'(newVal) {
            if (newVal === 'percentage' && this.form.discount_amount > 100) {
                this.form.discount_amount = 100
            }
        },
        hasSelectedLinkedItems(newVal) {
            if (newVal) {
                this.form.has_linked_items = true
            }
        },
    },
    async mounted() {
        this.form.start_date = new Date().toISOString().split('T')[0]
        this.form.expiration_date = null
        await this.fetchFormData()
        await this.fetchPromotion()
        this.initialForm = JSON.stringify(this.form)

        // Remove white background from parent container
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.add('promotion-v3-no-bg')
        }
    },
    beforeDestroy() {
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.remove('promotion-v3-no-bg')
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        normalizeSizeVertical,
        async fetchFormData() {
            try {
                const response = await axios.get('/api/v3/promotion/form-data', {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.formData = response.data.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        async fetchPromotion() {
            if (!this.promotionId) return
            this.loading = true
            try {
                const response = await axios.get(`/api/v3/promotion/${this.promotionId}`, {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                const p = response.data.data

                this.form = {
                    ...this.form,
                    active: !p.disabled,
                    title: p.title,
                    code: p.code,
                    description: p.description,
                    discount_type: p.discount_type,
                    discount_amount: p.discount_amount,
                    start_date: (p.start_date ?? p.created_at).split('T')[0],
                    expiration_date: p.expiration_date,
                    no_expiration_date: !p.expiration_date,
                    unlimited_uses: p.uses === null,
                    uses: p.uses,
                    uses_per_user: p.uses_per_user > 0 ? p.uses_per_user : 1,
                    min_amount: p.min_amount,
                    max_amount: p.max_amount,
                    back_office_only: p.back_office_only,
                    has_linked_items: !p.is_global,
                    prestation_categories: p.prestation_categories.map((c) => c.id),
                    has_prestation_categories: p.prestation_categories.length > 0 ?? false,
                    product_categories: p.product_categories.map((c) => c.id),
                    has_product_categories: p.product_categories.length > 0 ?? false,
                    food_categories: p.foods_categories ? p.foods_categories.map((c) => c.id) : [],
                    has_food_categories: p.foods_categories ? p.foods_categories.length > 0 ?? false : false,
                    has_voucher_categories: !!p.has_voucher,
                    limit_order_type:
                        p.prestation_categories.length > 0 ||
                        p.product_categories.length > 0 ||
                        (p.foods_categories && p.foods_categories.length > 0) ||
                        !!p.has_voucher,
                    prestations: p.prestations.map((i) => i.id),
                    products: p.products.map((i) => i.id),
                    packages: p.packages.map((i) => i.id),
                    series: p.series.map((i) => i.id),
                    treatments: p.treatments.map((i) => i.id),
                    foods: p.foods ? p.foods.map((i) => i.id) : [],
                    food_packs: p.food_packs ? p.food_packs.map((i) => i.id) : [],
                    used: p.used,
                    days: p.days ? p.days.map((d) => parseInt(d)) : [],
                    apply_to: p.apply_to || 'created_at',
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error fetching promotion:', error)
            } finally {
                this.loading = false
            }
        },
        generateRandomCode() {
            const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
            let result = ''
            for (let i = 0; i < 8; i++) {
                result += characters.charAt(Math.floor(Math.random() * characters.length))
            }
            this.form.code = result
            this.form.generate_randomly = false
        },
        cancel() {
            if (JSON.stringify(this.form) !== this.initialForm) {
                this.showExitPopup = true
            } else {
                this.exit()
            }
        },
        exit() {
            window.location.href = '/back-office/v3/promotions'
        },
        async submit() {
            this.submitting = true
            this.errors = {}
            if (!this.form.unlimited_uses && (this.form.uses === null || this.form.uses === '')) {
                this.errors.uses = [this.$t('promotions.validation_uses_required')]
                this.$toast.error(this.$t('common.formValidationError'))
                this.submitting = false
                return
            }
            try {
                const url = this.promotionId ? `/api/v3/promotion/${this.promotionId}` : '/api/v3/promotion/store'
                const method = this.promotionId ? 'put' : 'post'

                const data = {
                    ...this.form,
                    disabled: !this.form.active,
                    is_global: !this.form.has_linked_items,
                }

                await axios[method](url, data, { headers: { Authorization: `Bearer ${this.token}` } })

                this.$toast.success(this.$t('common.success'))
                this.initialForm = JSON.stringify(this.form)
                this.exit()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors
                    this.$toast.error(this.$t('common.formValidationError'))
                }
            } finally {
                this.submitting = false
            }
        },
        toggleDay(dayNumber) {
            const index = this.form.days.indexOf(dayNumber)
            if (index > -1) {
                this.form.days.splice(index, 1)
            } else {
                this.form.days.push(dayNumber)
            }
        },
    },
}
</script>

<style scoped lang="scss">
.promotion-v3-container {
    padding: 0;
}

.title-header {
    letter-spacing: -0.02em;
}

:deep(.v-application--wrap) {
    min-height: auto;
}

:deep(.v-card) {
    border-radius: 10px !important;
}

.promotion-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    padding: 12px 16px;
    transition: all 0.2s;

    &:hover {
        border-color: #7d2dbd;
    }
}

.row-left {
    display: flex;
    align-items: center;
    gap: 16px;
}

.icon-box {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid #7d2dbd;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7d2dbd;
}

.row-label {
    font-family: 'Poppins', sans-serif;
    font-size: 15px;
    color: black;
    font-weight: 400;
}

.row-right {
    display: flex;
    align-items: center;
}

.row-input {
    text-align: right;
    border: none;
    outline: none;
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    font-size: 15px;
    color: black;
    width: 150px;
    background: transparent;

    &::placeholder {
        color: #999;
        font-weight: 400;
    }
}

.row-select {
    border: none;
    outline: none;
    background: transparent;
    margin-right: 8px;
    font-family: 'Poppins', sans-serif;
}

.day-circle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #7d2dbd;
    border-radius: 50%;
    background: transparent;
    color: #7d2dbd;
    transition: all 0.2s ease-in-out;
    cursor: pointer;

    &:hover {
        background: rgba(125, 45, 189, 0.1);
    }

    &.active {
        background: #7d2dbd;
        color: white;
    }
}
</style>

<style lang="scss">
#main-container.promotion-v3-no-bg {
    background: transparent !important;
    border-radius: 0px;
}
</style>
